import React, { useState } from 'react';
import {
  Box,
  Flex,
  Button,
  Text,
  Stack,
  HStack,
  Badge,
  useColorModeValue,
  Heading,
  List,
  ListItem,
  ListIcon,
  Divider,
  Switch,
} from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import {
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_INTERVAL,
  PLAN_FEATURES,
} from '../../../constants/subscription';
import { useAtomValue } from 'jotai';
import { getPriceByPlanAndIntervalAtom } from '../../../atoms/v0/subscriptionAtoms';

type SubscriptionPlansProps = {
  isLoading?: boolean;
  activeSubscription?: any;
  onSelectPlan: (priceId: string) => void;
};

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  isLoading = false,
  activeSubscription,
  onSelectPlan
}) => {
  const { t } = useTranslation(['v0/subscription', 'v0/common', 'common']);
  const [billingInterval, setBillingInterval] = useState<string>(SUBSCRIPTION_INTERVAL.MONTH);
  const getPriceByPlanAndInterval = useAtomValue(getPriceByPlanAndIntervalAtom);

  const cardBg = useColorModeValue('#FFFFFF', '#1C1C1E');
  const cardBorder = useColorModeValue('#EBEBEB', '#2C2C2E');
  const featureColor = useColorModeValue('#767676', '#A0A0A0');
  const highlightColor = useColorModeValue('#FF5A5F', '#FF8C8D');
  const freeBgColor = useColorModeValue('#F8F8F8', '#2C2C2E');

  // 检查用户是否已订阅该计划
  const isSubscribed = (plan: string): boolean => {
    if (!activeSubscription) return false;
    return activeSubscription.planId === plan || activeSubscription.name === plan;
  };

  // 获取给定计划和周期的价格显示
  const getPriceDisplay = (plan: string, interval: string) => {
    const priceInfo = getPriceByPlanAndInterval(plan, interval);
    if (!priceInfo) return { formattedAmount: 'N/A', isDiscounted: false, originalAmount: null };

    const { formattedAmount, discount, amount } = priceInfo;
    return {
      formattedAmount,
      isDiscounted: !!discount,
      originalAmount: discount ? `¥${(amount / (1 - discount)).toFixed(2)}` : null
    };
  };

  // 获取计划特性列表
  const getPlanFeatures = (plan: string) => {
    return PLAN_FEATURES[plan] || [];
  };

  // 获取给定计划和周期的价格ID
  const getPriceId = (plan: string, interval: string): string | null => {
    const priceInfo = getPriceByPlanAndInterval(plan, interval);
    return priceInfo?.id || null;
  };

  // 处理周期切换
  const handleIntervalChange = () => {
    const newInterval = billingInterval === SUBSCRIPTION_INTERVAL.MONTH
      ? SUBSCRIPTION_INTERVAL.YEAR
      : SUBSCRIPTION_INTERVAL.MONTH;
    setBillingInterval(newInterval);
  };

  return (
    <Box width="100%" mx="auto">
      {/* 计费周期选择器 */}
      <Flex
        justify="center"
        mb={{ base: 5, md: 8 }}
        flexDir={{ base: 'column', sm: 'row' }}
        align="center"
        gap={{ base: 2, sm: 4 }}
      >
        <Text
          fontWeight="500"
          fontSize={{ base: 'sm', md: 'md' }}
          color={billingInterval === SUBSCRIPTION_INTERVAL.MONTH ? highlightColor : featureColor}
        >
          {t('v0/subscription:billing.monthly')}
        </Text>

        <Switch
          isChecked={billingInterval === SUBSCRIPTION_INTERVAL.YEAR}
          onChange={handleIntervalChange}
          colorScheme="red"
          size={{ base: 'md', md: 'lg' }}
        />

        <Flex align="center">
          <Text
            fontWeight="500"
            fontSize={{ base: 'sm', md: 'md' }}
            color={billingInterval === SUBSCRIPTION_INTERVAL.YEAR ? highlightColor : featureColor}
          >
            {t('v0/subscription:billing.yearly')}
          </Text>

          {billingInterval === SUBSCRIPTION_INTERVAL.YEAR && (
            <Badge
              ml={2}
              colorScheme="green"
              fontSize={{ base: 'xs', md: 'sm' }}
              borderRadius="full"
              px={2}
              py={0.5}
            >
              {t('v0/subscription:billing.savePercent', { percent: '17%' })}
            </Badge>
          )}
        </Flex>
      </Flex>

      {/* 计划卡片 - 移动响应式布局 */}
      <Stack
        direction={{ base: 'column', md: 'row' }}
        spacing={{ base: 6, md: 4 }}
        align="stretch"
      >
        {/* 免费计划 */}
        <Box
          borderWidth="1px"
          borderRadius="lg"
          overflow="hidden"
          p={{ base: 5, md: 6 }}
          bg={freeBgColor}
          borderColor={cardBorder}
          flex="1"
          maxW={{ base: '100%', md: '50%' }}
          boxShadow="sm"
        >
          <Stack spacing={4} h="100%">
            <Text
              fontSize="sm"
              fontWeight="500"
              letterSpacing="wide"
              color={featureColor}
              textTransform="uppercase"
            >
              {t('v0/subscription:plans.free.label')}
            </Text>
            <Heading
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="500"
            >
              {t('v0/subscription:plans.free.name')}
            </Heading>

            <Heading
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="500"
            >
              {t('v0/subscription:plans.free.price')}
            </Heading>

            <Divider />

            <List spacing={3} flex="1">
              <ListItem>
                <ListIcon as={FiCheck} color="green.500" boxSize={5} />
                <Text as="span" fontSize={{ base: 'sm', md: 'md' }}>
                  {t('v0/subscription:plans.free.features.taskLimit')}
                </Text>
              </ListItem>
              <ListItem>
                <ListIcon as={FiCheck} color="green.500" boxSize={5} />
                <Text as="span" fontSize={{ base: 'sm', md: 'md' }}>
                  {t('v0/subscription:plans.free.features.basicAi')}
                </Text>
              </ListItem>
            </List>

            <Button
              colorScheme="gray"
              variant="outline"
              size={{ base: 'md', md: 'lg' }}
              isDisabled={true}
              mt="auto"
              h={{ base: '44px', md: '50px' }}
              fontSize={{ base: 'sm', md: 'md' }}
              fontWeight="500"
            >
              {t('v0/subscription:actions.currentPlan')}
            </Button>
          </Stack>
        </Box>

        {/* Plus计划 */}
        <Box
          borderWidth="2px"
          borderRadius="lg"
          overflow="hidden"
          p={{ base: 5, md: 6 }}
          bg={cardBg}
          borderColor={highlightColor}
          flex="1"
          maxW={{ base: '100%', md: '50%' }}
          boxShadow="md"
          position="relative"
        >
          <Stack spacing={4} h="100%">
            <Text
              fontSize="sm"
              fontWeight="500"
              letterSpacing="wide"
              color={highlightColor}
              textTransform="uppercase"
            >
              {t('v0/subscription:plans.plus.badge')}
            </Text>

            <Heading
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="500"
            >
              {t('v0/subscription:plans.plus.name')}
            </Heading>

            {/* 价格显示 */}
            <Box>
              <Flex align="baseline">
                <Heading
                  fontSize={{ base: '3xl', md: '4xl' }}
                  fontWeight="500"
                >
                  {getPriceDisplay(SUBSCRIPTION_PLAN.PLUS, billingInterval).formattedAmount}
                </Heading>
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  fontWeight="400"
                  color={featureColor}
                  ml={1}
                >
                  /{billingInterval === SUBSCRIPTION_INTERVAL.MONTH ?
                    t('v0/subscription:billing.mo') :
                    t('v0/subscription:billing.yr')}
                </Text>
              </Flex>

              {/* 折扣价格 */}
              {getPriceDisplay(SUBSCRIPTION_PLAN.PLUS, billingInterval).isDiscounted && (
                <Text fontSize="sm" color={featureColor} textDecoration="line-through">
                  {getPriceDisplay(SUBSCRIPTION_PLAN.PLUS, billingInterval).originalAmount}
                </Text>
              )}
            </Box>

            <Divider />

            {/* 特性列表 */}
            <List spacing={3} flex="1">
              {getPlanFeatures(SUBSCRIPTION_PLAN.PLUS).map((feature) => (
                <ListItem key={feature}>
                  <ListIcon as={FiCheck} color="green.500" boxSize={5} />
                  <Text as="span" fontSize={{ base: 'sm', md: 'md' }}>
                    {t(`v0/subscription:features.${feature}`)}
                  </Text>
                </ListItem>
              ))}
            </List>

            {/* 订阅按钮 */}
            {isSubscribed(SUBSCRIPTION_PLAN.PLUS) ? (
              <Button
                colorScheme="gray"
                variant="outline"
                size={{ base: 'md', md: 'lg' }}
                isDisabled={true}
                mt="auto"
                h={{ base: '44px', md: '50px' }}
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="500"
              >
                {t('v0/subscription:actions.currentPlan')}
              </Button>
            ) : (
              <Button
                bg={highlightColor}
                _hover={{ bg: useColorModeValue('#E34850', '#FF6C70') }}
                color="white"
                size={{ base: 'md', md: 'lg' }}
                isLoading={isLoading}
                onClick={() => {
                  const priceId = getPriceId(SUBSCRIPTION_PLAN.PLUS, billingInterval);
                  if (priceId) {
                    onSelectPlan(priceId);
                  } else {
                    console.error('Could not find price ID for plan:', SUBSCRIPTION_PLAN.PLUS, 'interval:', billingInterval);
                    // TODO: Show error toast to user
                  }
                }}
                mt="auto"
                h={{ base: '44px', md: '50px' }}
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="500"
              >
                {t('v0/subscription:actions.subscribe')}
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default SubscriptionPlans;